import React, { useState, useEffect } from 'react';
import { X, Code, FileCode, Braces, AlertCircle, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface CustomCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  headCode: string;
  bodyStartCode: string;
  bodyEndCode: string;
  customCss: string;
  onSave: (codes: {
    headCode: string;
    bodyStartCode: string;
    bodyEndCode: string;
    customCss: string;
  }) => void;
}

type TabType = 'head' | 'bodyStart' | 'bodyEnd' | 'css';

const CustomCodeModal: React.FC<CustomCodeModalProps> = ({
  isOpen,
  onClose,
  headCode: initialHeadCode,
  bodyStartCode: initialBodyStartCode,
  bodyEndCode: initialBodyEndCode,
  customCss: initialCustomCss,
  onSave
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabType>('head');
  const [headCode, setHeadCode] = useState(initialHeadCode);
  const [bodyStartCode, setBodyStartCode] = useState(initialBodyStartCode);
  const [bodyEndCode, setBodyEndCode] = useState(initialBodyEndCode);
  const [customCss, setCustomCss] = useState(initialCustomCss);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setHeadCode(initialHeadCode);
      setBodyStartCode(initialBodyStartCode);
      setBodyEndCode(initialBodyEndCode);
      setCustomCss(initialCustomCss);
      setSaved(false);
    }
  }, [isOpen, initialHeadCode, initialBodyStartCode, initialBodyEndCode, initialCustomCss]);

  if (!isOpen) return null;

  const handleSave = () => {
    onSave({ headCode, bodyStartCode, bodyEndCode, customCss });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const tabs = [
    { id: 'head' as TabType, label: 'Head', icon: FileCode, description: t('pageBuilder.customCode.headDesc', 'Scripts no <head>') },
    { id: 'bodyStart' as TabType, label: 'Body Start', icon: Code, description: t('pageBuilder.customCode.bodyStartDesc', 'Inicio do <body>') },
    { id: 'bodyEnd' as TabType, label: 'Body End', icon: Code, description: t('pageBuilder.customCode.bodyEndDesc', 'Fim do <body>') },
    { id: 'css' as TabType, label: 'CSS', icon: Braces, description: t('pageBuilder.customCode.cssDesc', 'Estilos customizados') }
  ];

  const getCodeValue = () => {
    switch (activeTab) {
      case 'head': return headCode;
      case 'bodyStart': return bodyStartCode;
      case 'bodyEnd': return bodyEndCode;
      case 'css': return customCss;
    }
  };

  const setCodeValue = (value: string) => {
    switch (activeTab) {
      case 'head': setHeadCode(value); break;
      case 'bodyStart': setBodyStartCode(value); break;
      case 'bodyEnd': setBodyEndCode(value); break;
      case 'css': setCustomCss(value); break;
    }
  };

  const getPlaceholder = () => {
    switch (activeTab) {
      case 'head':
        return `<!-- Google Analytics, Meta Pixel, etc -->
<script async src="https://www.googletagmanager.com/gtag/js?id=GA_ID"></script>
<script>
  window.dataLayer = window.dataLayer || [];
  function gtag(){dataLayer.push(arguments);}
  gtag('js', new Date());
  gtag('config', 'GA_ID');
</script>`;
      case 'bodyStart':
        return `<!-- Google Tag Manager (noscript) -->
<noscript><iframe src="https://www.googletagmanager.com/ns.html?id=GTM_ID"
height="0" width="0" style="display:none;visibility:hidden"></iframe></noscript>`;
      case 'bodyEnd':
        return `<!-- Chat widgets, conversion scripts, etc -->
<script src="https://widget.example.com/chat.js"></script>`;
      case 'css':
        return `/* CSS customizado */
.my-custom-class {
  color: #333;
  font-size: 16px;
}

/* Esconder elemento em mobile */
@media (max-width: 768px) {
  .hide-mobile {
    display: none;
  }
}`;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-gray-900 rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl border border-gray-800">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-800">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                <Code className="w-5 h-5 text-white" />
              </div>
              {t('pageBuilder.customCode.title', 'Codigo Customizado')}
            </h2>
            <p className="text-gray-400 text-sm mt-1">
              {t('pageBuilder.customCode.subtitle', 'Adicione scripts de rastreamento, pixels e CSS personalizado')}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-800 rounded-lg transition-colors text-gray-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-800 px-6">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 font-medium text-sm transition-all border-b-2 -mb-px ${
                  activeTab === tab.id
                    ? 'text-white border-indigo-500 bg-indigo-500/10'
                    : 'text-gray-500 border-transparent hover:text-gray-300 hover:bg-gray-800/50'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="flex-1 p-6 overflow-auto">
          {/* Info Box */}
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 mb-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-blue-300 text-sm font-medium">
                {tabs.find(t => t.id === activeTab)?.description}
              </p>
              <p className="text-blue-400/70 text-xs mt-1">
                {activeTab === 'head' && t('pageBuilder.customCode.headHelp', 'Use para Google Analytics, Meta Pixel, Google Tag Manager, etc.')}
                {activeTab === 'bodyStart' && t('pageBuilder.customCode.bodyStartHelp', 'Use para scripts que precisam carregar logo apos o <body>.')}
                {activeTab === 'bodyEnd' && t('pageBuilder.customCode.bodyEndHelp', 'Use para widgets de chat, scripts de conversao, etc.')}
                {activeTab === 'css' && t('pageBuilder.customCode.cssHelp', 'Use para estilos CSS personalizados que serao aplicados na pagina.')}
              </p>
            </div>
          </div>

          {/* Code Editor */}
          <div className="relative">
            <textarea
              value={getCodeValue()}
              onChange={(e) => setCodeValue(e.target.value)}
              placeholder={getPlaceholder()}
              className="w-full h-80 bg-gray-950 border border-gray-700 rounded-xl p-4 text-gray-300 font-mono text-sm resize-none focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 placeholder-gray-600"
              spellCheck={false}
            />
            <div className="absolute top-3 right-3 text-xs text-gray-600 uppercase tracking-wider">
              {activeTab === 'css' ? 'CSS' : 'HTML/JS'}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-800 bg-gray-900/50">
          <p className="text-gray-500 text-sm">
            {t('pageBuilder.customCode.warning', 'Estes codigos serao incluidos quando a pagina for publicada')}
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-400 hover:text-white font-medium transition-colors"
            >
              {t('common.cancel', 'Cancelar')}
            </button>
            <button
              onClick={handleSave}
              className="px-6 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-semibold rounded-xl transition-all flex items-center gap-2"
            >
              {saved ? (
                <>
                  <Check className="w-4 h-4" />
                  {t('common.saved', 'Salvo!')}
                </>
              ) : (
                t('common.save', 'Salvar')
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomCodeModal;
